import React, { useState, useEffect } from 'react';
import { Settings, Bell, BellOff, Volume2, VolumeX, Monitor } from 'lucide-react';

interface NotificationSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  onSettingsChange: (settings: NotificationSettings) => void;
  currentSettings: NotificationSettings;
}

export interface NotificationSettings {
  enableBrowserNotifications: boolean;
  enableSoundNotifications: boolean;
  enableDesktopNotifications: boolean;
  enableMobileNotifications: boolean;
  notificationTypes: {
    audit_events: boolean;
    security_events: boolean;
    user_actions: boolean;
    system_events: boolean;
    bulk_operations: boolean;
    exports: boolean;
  };
  quietHours: {
    enabled: boolean;
    startTime: string;
    endTime: string;
  };
  maxNotifications: number;
}

const defaultSettings: NotificationSettings = {
  enableBrowserNotifications: true,
  enableSoundNotifications: false,
  enableDesktopNotifications: true,
  enableMobileNotifications: false,
  notificationTypes: {
    audit_events: true,
    security_events: true,
    user_actions: true,
    system_events: true,
    bulk_operations: true,
    exports: true,
  },
  quietHours: {
    enabled: false,
    startTime: '22:00',
    endTime: '08:00',
  },
  maxNotifications: 50,
};

const NotificationSettingsComponent: React.FC<NotificationSettingsProps> = ({
  isOpen,
  onClose,
  onSettingsChange,
  currentSettings
}) => {
  const [settings, setSettings] = useState<NotificationSettings>(currentSettings || defaultSettings);
  const [browserPermission, setBrowserPermission] = useState<NotificationPermission>('default');

  useEffect(() => {
    if (typeof Notification !== 'undefined') {
      setBrowserPermission(Notification.permission);
    }
  }, []);

  useEffect(() => {
    setSettings(currentSettings || defaultSettings);
  }, [currentSettings]);

  const handleSettingChange = (key: keyof NotificationSettings, value: any) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    onSettingsChange(newSettings);
  };

  const handleNotificationTypeChange = (type: keyof NotificationSettings['notificationTypes'], enabled: boolean) => {
    const newTypes = { ...settings.notificationTypes, [type]: enabled };
    const newSettings = { ...settings, notificationTypes: newTypes };
    setSettings(newSettings);
    onSettingsChange(newSettings);
  };

  const handleQuietHoursChange = (key: keyof NotificationSettings['quietHours'], value: any) => {
    const newQuietHours = { ...settings.quietHours, [key]: value };
    const newSettings = { ...settings, quietHours: newQuietHours };
    setSettings(newSettings);
    onSettingsChange(newSettings);
  };

  const requestBrowserPermission = async () => {
    if (typeof Notification === 'undefined') {
      return;
    }

    if (Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      setBrowserPermission(permission);
      if (permission === 'granted') {
        handleSettingChange('enableBrowserNotifications', true);
      }
    }
  };

  const testNotification = () => {
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      new Notification('Test Notification', {
        body: 'This is a test notification from the Pricing System',
        icon: '/favicon.ico'
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Settings className="w-6 h-6 text-gray-600" />
              <h2 className="text-xl font-semibold text-gray-900">Notification Settings</h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* General Settings */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">General Settings</h3>
            <div className="space-y-4">
              {/* Browser Notifications */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {settings.enableBrowserNotifications ? (
                    <Bell className="w-5 h-5 text-blue-500" />
                  ) : (
                    <BellOff className="w-5 h-5 text-gray-400" />
                  )}
                  <div>
                    <label className="text-sm font-medium text-gray-700">Browser Notifications</label>
                    <p className="text-xs text-gray-500">Show notifications in your browser</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {browserPermission === 'denied' && (
                    <span className="text-xs text-red-500">Blocked</span>
                  )}
                  {browserPermission === 'default' && (
                    <button
                      onClick={requestBrowserPermission}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      Enable
                    </button>
                  )}
                  <input
                    type="checkbox"
                    checked={settings.enableBrowserNotifications && browserPermission === 'granted'}
                    onChange={(e) => handleSettingChange('enableBrowserNotifications', e.target.checked)}
                    disabled={browserPermission !== 'granted'}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Sound Notifications */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {settings.enableSoundNotifications ? (
                    <Volume2 className="w-5 h-5 text-blue-500" />
                  ) : (
                    <VolumeX className="w-5 h-5 text-gray-400" />
                  )}
                  <div>
                    <label className="text-sm font-medium text-gray-700">Sound Notifications</label>
                    <p className="text-xs text-gray-500">Play sound for notifications</p>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={settings.enableSoundNotifications}
                  onChange={(e) => handleSettingChange('enableSoundNotifications', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </div>

              {/* Desktop Notifications */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Monitor className="w-5 h-5 text-blue-500" />
                  <div>
                    <label className="text-sm font-medium text-gray-700">Desktop Notifications</label>
                    <p className="text-xs text-gray-500">Show notifications on desktop</p>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={settings.enableDesktopNotifications}
                  onChange={(e) => handleSettingChange('enableDesktopNotifications', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </div>

              {/* Max Notifications */}
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-700">Maximum Notifications</label>
                  <p className="text-xs text-gray-500">Maximum number of notifications to keep</p>
                </div>
                <select
                  value={settings.maxNotifications}
                  onChange={(e) => handleSettingChange('maxNotifications', parseInt(e.target.value))}
                  className="border border-gray-300 rounded-md px-3 py-1 text-sm"
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                </select>
              </div>
            </div>
          </div>

          {/* Notification Types */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Notification Types</h3>
            <div className="space-y-3">
              {Object.entries(settings.notificationTypes).map(([type, enabled]) => (
                <div key={type} className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700 capitalize">
                      {type.replace('_', ' ')}
                    </label>
                    <p className="text-xs text-gray-500">
                      {getNotificationTypeDescription(type)}
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={enabled}
                    onChange={(e) => handleNotificationTypeChange(
                      type as keyof NotificationSettings['notificationTypes'],
                      e.target.checked
                    )}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Quiet Hours */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Quiet Hours</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-700">Enable Quiet Hours</label>
                  <p className="text-xs text-gray-500">Disable notifications during specified hours</p>
                </div>
                <input
                  type="checkbox"
                  checked={settings.quietHours.enabled}
                  onChange={(e) => handleQuietHoursChange('enabled', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </div>

              {settings.quietHours.enabled && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                    <input
                      type="time"
                      value={settings.quietHours.startTime}
                      onChange={(e) => handleQuietHoursChange('startTime', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                    <input
                      type="time"
                      value={settings.quietHours.endTime}
                      onChange={(e) => handleQuietHoursChange('endTime', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex items-center justify-between">
          <button
            onClick={testNotification}
            disabled={!settings.enableBrowserNotifications || browserPermission !== 'granted'}
            className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 disabled:text-gray-400"
          >
            Test Notification
          </button>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
            >
              Cancel
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
            >
              Save Settings
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const getNotificationTypeDescription = (type: string): string => {
  switch (type) {
    case 'audit_events':
      return 'User actions and system changes';
    case 'security_events':
      return 'Security alerts and suspicious activities';
    case 'user_actions':
      return 'User-specific notifications';
    case 'system_events':
      return 'System status and maintenance';
    case 'bulk_operations':
      return 'Bulk operation completion status';
    case 'exports':
      return 'Export completion notifications';
    default:
      return 'Notification type';
  }
};

export default NotificationSettingsComponent;
